import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, catchError, filter, map, Observable, take, tap, throwError, timeout } from 'rxjs';
import { ApiClientService } from '@services/api-client/api-client.service';
import { LoginRequest, RefreshTokenRequest } from '@services/api-client/requests';
import { LoginResponse } from '@services/api-client/responses';
import { SessionStorageKey, SessionStorageService } from '@services/session-storage';
import { AuthenticatedUser } from 'app/types/index';
import { PermissionService } from '@services/permission/permission.service';
import { CachedValuesService } from '@services/cached-values';
import { TablePreferencesService } from '@services/local-storage/table-preferences.service';
import { CrossTabCommunicationService } from '@services/cross-tab/cross-tab-communication.service';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {
    private readonly userSubject: BehaviorSubject<AuthenticatedUser | null>;
    public user$: Observable<AuthenticatedUser | null>;

    private isRefreshing = false;
    private readonly refreshTokenSubject = new BehaviorSubject<unknown>(null);

    private readonly syncReadySubject = new BehaviorSubject(false);
    public syncReady$: Observable<boolean> = this.syncReadySubject.asObservable();

    private readonly apiClientService = inject(ApiClientService);
    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly permissionService = inject(PermissionService);
    private readonly cachedValuesService = inject(CachedValuesService);
    private readonly tablePreferencesService = inject(TablePreferencesService);
    private readonly crossTabService = inject(CrossTabCommunicationService);

    public constructor() {
        this.userSubject = new BehaviorSubject(this.getUser());
        this.user$ = this.userSubject.asObservable();
        this.setupCrossTabListeners();
    }

    /**
     * Setup listeners for cross-tab communication events
     */
    private setupCrossTabListeners(): void {
        // Handle logout from other tabs
        this.crossTabService.logout$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.handleRemoteLogout();
        });

        // Handle token updates from other tabs
        this.crossTabService.tokenUpdated$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((message) => {
            this.handleRemoteTokenUpdate(message.data.token, message.data.refreshToken);
        });

        // Handle sync requests from newly opened tabs
        this.crossTabService.syncRequest$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            const token = this.getToken();
            const refreshToken = this.getRefreshToken();
            const user = this.getUser();
            this.crossTabService.broadcastSyncResponse(token || null, refreshToken || null, user || null);
        });

        // On initial load, try to sync with other tabs if no token
        if (!this.getToken()) {
            this.attemptCrossTabSync();
        } else {
            // Already authenticated, no need to wait for sync
            this.syncReadySubject.next(true);
        }
    }

    /**
     * Attempt to sync authentication state from other tabs
     */
    private attemptCrossTabSync(): void {
        // Broadcast sync request to other tabs
        this.crossTabService.broadcastSyncRequest();

        // Listen for sync responses
        this.crossTabService.syncResponse$
            .pipe(
                timeout(1000), // Wait max 1 second for response
                take(1),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (message) => {
                    if (message.data.token && message.data.refreshToken) {
                        this.sessionStorageService.setItem(SessionStorageKey.Token, message.data.token);
                        this.sessionStorageService.setItem(SessionStorageKey.RefreshToken, message.data.refreshToken);
                        if (message.data.user) {
                            this.sessionStorageService.setItem(SessionStorageKey.User, message.data.user);
                            this.userSubject.next(message.data.user);
                            // Refresh permission service with the synced user data
                            this.permissionService.setUserPermissions();
                        }
                    }
                    this.syncReadySubject.next(true);
                },
                error: () => {
                    // Timeout or error - proceed without sync (user will be redirected to login)
                    this.syncReadySubject.next(true);
                }
            });
    }

    /**
     * Handle logout triggered by another tab
     */
    private handleRemoteLogout(): void {
        // Call internal logout without broadcasting to prevent infinite loop
        this.performLogout(false, false);
    }

    /**
     * Handle token update from another tab
     */
    private handleRemoteTokenUpdate(token: string, refreshToken: string): void {
        this.sessionStorageService.setItem(SessionStorageKey.Token, token);
        this.sessionStorageService.setItem(SessionStorageKey.RefreshToken, refreshToken);
    }

    public isAuthenticated() {
        return !!this.getToken();
    }

    public getToken() {
        return this.sessionStorageService.getItem<string>(SessionStorageKey.Token);
    }

    public getRefreshToken() {
        return this.sessionStorageService.getItem<string>(SessionStorageKey.RefreshToken);
    }

    public renewToken(refreshToken: string): Observable<unknown> {
        if (this.isRefreshing) {
            return this.refreshTokenSubject.pipe(
                filter((result) => result !== null),
                take(1)
            );
        }

        this.isRefreshing = true;
        this.refreshTokenSubject.next(null);

        const request: RefreshTokenRequest = { refreshToken };
        return this.apiClientService.refreshToken$(request).pipe(
            tap((response) => {
                this.isRefreshing = false;
                this.refreshTokenSubject.next(response);
            }),
            catchError((err: unknown) => {
                this.isRefreshing = false;
                return throwError(() => err);
            })
        );
    }

    public getUser() {
        return this.sessionStorageService.getItem<AuthenticatedUser>(SessionStorageKey.User);
    }

    public storeTokens(response: LoginResponse) {
        this.sessionStorageService.setItem(SessionStorageKey.Token, response.jwtToken);
        this.sessionStorageService.setItem(SessionStorageKey.RefreshToken, response.refreshToken);
        // Broadcast token update to other tabs
        this.crossTabService.broadcastTokenUpdate(response.jwtToken, response.refreshToken);
    }

    public removeTokens() {
        this.sessionStorageService.removeItem(SessionStorageKey.Token);
        this.sessionStorageService.removeItem(SessionStorageKey.RefreshToken);
    }

    public login(request: LoginRequest) {
        return this.apiClientService.login$(request).pipe(
            takeUntilDestroyed(this.destroyRef),
            map((response) => response as LoginResponse),
            map((response) => {
                const user = this.createUserObject(response);
                this.storeTokens(response);
                this.sessionStorageService.setItem(SessionStorageKey.User, user);
                this.userSubject.next(user);
                this.permissionService.setUserPermissions();

                return user;
            })
        );
    }

    public changeRepresentation(representationId: string) {
        return this.apiClientService.changeRepresentation$(representationId).pipe(
            takeUntilDestroyed(this.destroyRef),
            map((response) => response as LoginResponse),
            map((response) => {
                this.logout(false);
                const user = this.createUserObject(response);
                this.storeTokens(response);
                this.sessionStorageService.setItem(SessionStorageKey.User, user);
                this.userSubject.next(user);
                this.permissionService.setUserPermissions();

                return user;
            })
        );
    }

    /**
     * Public logout method - broadcasts to other tabs and optionally notifies server
     */
    public logout(notifyServer = true) {
        this.performLogout(notifyServer, true);
    }

    /**
     * Internal logout implementation
     * @param notifyServer - Whether to send logout request to server
     * @param broadcast - Whether to broadcast logout to other tabs
     */
    private performLogout(notifyServer: boolean, broadcast: boolean): void {
        // Broadcast first before clearing tokens (so other tabs can still verify the message)
        if (broadcast) {
            this.crossTabService.broadcastLogout();
        }

        if (notifyServer) {
            this.apiClientService.logout$().subscribe();
        }

        this.userSubject.next(null);
        this.sessionStorageService.removeAll();
        this.cachedValuesService.invalidateCache();
        this.tablePreferencesService.clearAll();
    }

    private createUserObject(response: LoginResponse): AuthenticatedUser {
        const { userId, fullname, permissions, username, representing, representationAlternatives } = response;

        return {
            userId: userId,
            fullname: fullname,
            permissions: permissions,
            username: username,
            representing: representing,
            representationAlternatives: representationAlternatives
        };
    }
}
